import {useConfig} from '@aboutyou/config';
import {styled} from '@linaria/react';
import React, {useMemo} from 'react';
import {COLORS} from '../../../constants/colors';
import {mobileOnlyLinaria} from '../../../constants/customMedia';
import {FONT_WEIGHTS} from '../../../constants/typography';
import {Flyout} from '../../atoms/Flyout/Flyout';
import {ModalDialog} from '../../atoms/ModalDialog/ModalDialog';
import {Paragraph10} from '../../atoms/Text';
import {ColorBubbles} from '../ColorBubbles/ColorBubbles';
import {LowestProductPrice30D} from '../LowestProductPrice30D/LowestProductPrice30D';
import {LowestProductPrice30DMoreInfo} from '../LowestProductPrice30D/LowestProductPrice30DMoreInfo';
import {LowestProductPrice30DWithRichText} from '../LowestProductPrice30D/LowestProductPrice30DWithRichText';
import {OriginalPrice} from '../OriginalPrice/OriginalPrice';
import {PriceBoxHorizontal} from '../PriceBoxHorizontal/PriceBoxHorizontal';
import {Sponsored} from '../ProductTile/Sponsored/Sponsored';
import {BaseProductTileProps} from './BaseProductTile';

export const ProductDetails: React.FC<
  React.PropsWithChildren<
    Pick<
      BaseProductTileProps,
      | 'productName'
      | 'brandName'
      | 'colors'
      | 'price'
      | 'onColorHover'
      | 'onColorLeave'
      | 'onColorClick'
      | 'productId'
      | 'sponsored'
      | 'isSponsoredProductTileUnsampledLoggingEnabled'
    >
  >
> = ({
  brandName,
  children,
  colors,
  onColorClick,
  onColorHover,
  onColorLeave,
  price,
  productId,
  productName,
  sponsored,
  isSponsoredProductTileUnsampledLoggingEnabled,
}) => {
  const {device} = useConfig();
  const LPL30DContent = useMemo(() => {
    if ('finalPrice' in price) {
      return (
        <>
          <StyledLowestProductPrice30DWithRichText lpl30d={price.lpl30d} />
          <LowestProductPrice30DMoreInfo
            lpl30d={price.lpl30d}
            lowestPrice30DProductStateManagerKey={`productDetail-${productId}`}
            showCloseButton={device === 'mobile'}
            LayoutComponent={device === 'mobile' ? Flyout : ModalDialog}
          />
        </>
      );
    }

    if (price.lpl30) {
      return <LowestProductPrice30D label={price.lpl30} />;
    }

    return null;
  }, [price, device, productId]);

  const isOriginalPricePresent =
    'finalPrice' in price && price.original ? 'true' : 'false';

  return (
    <ProductDetailsWrapper>
      {brandName && <BrandName data-testid="brandName">{brandName}</BrandName>}
      {productName && (
        <ProductName data-testid="productName">{productName}</ProductName>
      )}
      <PriceBoxHorizontal
        secondaryPriceLabel={
          'finalPrice' in price
            ? price.finalPrice.secondaryPriceLabel?.text
            : price.secondaryPriceLabel
        }
        reductions={'finalPrice' in price ? [] : price.reductions}
        price={price}
      />
      {device === 'mobile' && (
        <AdditionalProductInformation
          data-original-price={isOriginalPricePresent}
          data-mobile-version="true"
        >
          <StyledOriginalPrice price={price} />
          {LPL30DContent}
        </AdditionalProductInformation>
      )}
      {colors.length > 0 ? (
        <ColorContainer>
          <OverflowHidden>
            <ColorBubbles
              colorBubbles={colors}
              onColorClick={onColorClick}
              onColorLeave={onColorLeave}
              onColorHover={onColorHover}
              activeProductId={productId}
            />
          </OverflowHidden>
        </ColorContainer>
      ) : null}
      <AddToBasketCtaContainer>
        <OverflowHidden>{children}</OverflowHidden>
      </AddToBasketCtaContainer>
      <AdditionalProductInformation
        data-original-price={isOriginalPricePresent}
      >
        {device === 'desktop' && (
          <>
            <StyledOriginalPrice price={price} />
            {LPL30DContent}
          </>
        )}

        <Sponsored
          {...sponsored}
          skipRenderingText={!sponsored}
          isInProductTile
          isSponsoredProductTileUnsampledLoggingEnabled={
            isSponsoredProductTileUnsampledLoggingEnabled
          }
        />
      </AdditionalProductInformation>
    </ProductDetailsWrapper>
  );
};

const StyledOriginalPrice = styled(OriginalPrice)`
  justify-content: center;
`;

export const ColorContainer = styled.div`
  padding-top: 8px;
`;

const OverflowHidden = styled.div`
  overflow: hidden;
`;

export const AddToBasketCtaContainer = styled.div`
  &:hover {
    ${OverflowHidden} {
      overflow: visible;
    }
  }
`;

const BrandName = styled(Paragraph10)`
  margin: 0;
  padding: 0;
  font-weight: ${FONT_WEIGHTS.bold};
  text-transform: uppercase;
  text-overflow: ellipsis;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: 0.4px;
  color: inherit;
  line-height: 1.8;

  ${mobileOnlyLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
  `)}
`;

const ProductName = styled(Paragraph10)`
  padding: 0;
  color: ${COLORS.Grey1};
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  letter-spacing: 0.4px;
  margin: 0 0 0.25rem;
  line-height: 1.6;
  text-align: center;

  ${mobileOnlyLinaria(`
    font-weight: ${FONT_WEIGHTS.mediumBold};
    margin: 0;
  `)}
`;

export const ProductDetailsWrapper = styled.div`
  padding: 12px 8px;

  ${mobileOnlyLinaria(`
    text-align: center;
    padding: 16px 8px;
`)};
`;

const AdditionalProductInformation = styled.div`
  padding-top: 8px;

  &[data-original-price='true'] {
    padding-top: 10px;
  }

  &[data-mobile-version='true'] {
    padding-top: 2px;
  }
`;

const StyledLowestProductPrice30DWithRichText = styled(
  LowestProductPrice30DWithRichText,
)`
  justify-content: center;
`;
